import React, { useState } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useAuth } from '../../contexts/AuthContext';
import { PaperdollComposer, DEFAULT_PAPERDOLL } from './CharacterSetup';

// Standalone "Edit Appearance" modal — lets a player swap paperdoll layers,
// tints, scale, and chosen weapon mid-campaign without going through the full
// 3-step CharacterSetup flow. Writes to both the player record and the
// player's token so the map sprite updates in the same Firestore tick.

export default function AppearanceEditor({ onClose }) {
  const { players, tokens, updatePlayer, updateToken } = useCampaign();
  const { auth } = useAuth();
  const playerId = auth?.playerId;
  const playerRecord = playerId ? players?.[playerId] : null;

  const initialPaperdoll = (() => {
    const src = playerRecord?.paperdoll;
    if (!src) return DEFAULT_PAPERDOLL;
    // Be defensive against legacy shapes (the same migration CharacterSetup
    // does on pre-populate). Without this, players who created characters
    // before the tints/scale fields existed would crash the renderer.
    const pd = { ...src };
    if (!pd.tints) pd.tints = {};
    if (pd.widthScale  == null) pd.widthScale  = 1;
    if (pd.heightScale == null) pd.heightScale = 1;
    if (!pd.layers) pd.layers = { ...DEFAULT_PAPERDOLL.layers };
    return pd;
  })();
  const initialStyle = playerRecord?.combatStyle || 'melee';

  const [paperdoll,   setPaperdoll]   = useState(initialPaperdoll);
  const [combatStyle, setCombatStyle] = useState(initialStyle);
  const [saving,      setSaving]      = useState(false);

  const handleSave = async () => {
    if (!playerId) return;
    setSaving(true);
    try {
      await updatePlayer(playerId, { paperdoll, combatStyle });
      const myToken = Object.values(tokens || {}).find(
        (t) => t.type === 'player' && t.ownerId === playerId
      );
      if (myToken) {
        await updateToken(myToken.id, { paperdoll, combatStyle });
      }
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>🎨 Edit Appearance</span>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={styles.body}>
          <PaperdollComposer
            value={paperdoll}
            onChange={setPaperdoll}
            combatStyle={combatStyle}
            onCombatStyleChange={setCombatStyle}
          />
        </div>
        <div style={styles.footer}>
          <button className="btn-ghost" style={{ flex: 1 }} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Appearance'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay:  { position: 'absolute', inset: 0, background: 'rgba(5,8,18,0.8)', zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 },
  panel:    { background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, width: '100%', maxWidth: 620, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.7)', overflow: 'hidden' },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '2px solid #4a3d28', flexShrink: 0 },
  title:    { fontWeight: 800, fontSize: 15, fontFamily: "'Press Start 2P', monospace", color: '#d4a847' },
  closeBtn: { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 18, cursor: 'pointer' },
  body:     { padding: '12px 14px', overflowY: 'auto', flex: 1 },
  footer:   { display: 'flex', gap: 8, padding: '12px 18px', borderTop: '2px solid #4a3d28', flexShrink: 0 },
};
